/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../../components/sidebar/Sidebar2";
import Navbar from "../../components/navbar/Navbar2";
import Footer from "../../components/footer/Footer";
import Dhd from "../../components/SystemDhd";
import ValidationDhd from "./ValidationDhd";
import DhdPartnershipOffer, {
  DHD_PLAN_NAME,
} from "../../components/DhdPartnershipOffer";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUserDhdStatus, getDhdForm } from "../../utils/dhdHelpers";
import {
  Handshake,
  FileDown,
  BadgeCheck,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

const SpecialOffer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const user = useSelector((state) => state.userReducer.user);
  const account = useSelector((state) => state.AccountReducer || {});
  const [showSideBar, setShowSideBar] = useState(false);
  const [isDhdOpen, setIsDhdOpen] = useState(false);
  const [dhdStatus, setDhdStatus] = useState("none"); // none | in_progress | pending | approved | rejected
  const [isChecking, setIsChecking] = useState(true);
  const [localSub, setLocalSub] = useState(account?.subscription || null);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    setLocalSub(account?.subscription || null);
  }, [account?.subscription]);

  useEffect(() => {
    function handleResize() {
      setShowSideBar(window.innerWidth > 1080);
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!user) {
      setDhdStatus("none");
      setWarnings([]);
      setIsChecking(false);
      return;
    }

    setIsChecking(true);
    try {
      const { status, form } = await getUserDhdStatus(user);
      setDhdStatus(status === "rejected" ? "none" : status);

      if (status === "approved" && form) {
        const list = Array.isArray(form.warnings) ? form.warnings : [];
        setWarnings(
          [...list].sort((a, b) => {
            const ta = a?.createdAt?.seconds ?? 0;
            const tb = b?.createdAt?.seconds ?? 0;
            return tb - ta;
          }),
        );
      } else {
        setWarnings([]);
      }
    } catch (error) {
      console.error("Error checking DHD registration:", error);
      setDhdStatus("none");
      setWarnings([]);
    } finally {
      setIsChecking(false);
    }
  }, [user]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const handleDhdComplete = useCallback(() => {
    setDhdStatus("in_progress");
    setIsDhdOpen(false);
    window.dispatchEvent(
      new CustomEvent("dhd-registration-started", {
        detail: { at: Date.now() },
      }),
    );
  }, []);

  const steps = [
    {
      icon: Handshake,
      title: t("specialOfferPage.steps.register.title"),
      desc: t("specialOfferPage.steps.register.desc"),
    },
    {
      icon: FileDown,
      title: t("specialOfferPage.steps.download.title"),
      desc: t("specialOfferPage.steps.download.desc"),
    },
    {
      icon: BadgeCheck,
      title: t("specialOfferPage.steps.confirm.title"),
      desc: t("specialOfferPage.steps.confirm.desc"),
    },
  ];

  const isActive = dhdStatus === "in_progress" || dhdStatus === "pending";
  const isApproved =
    dhdStatus === "approved" ||
    localSub?.dhdPartnership === true ||
    localSub?.plan === DHD_PLAN_NAME;

  return (
    <div
      className="flex min-h-screen bg-gray-50 dark:!bg-[#141414]"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {showSideBar && (
        <div
          style={{
            width: "250px",
            flexShrink: 0,
            transition: "width 0.6s ease",
          }}
        >
          <Sidebar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />
        </div>
      )}

      <div
        className="px-3 w-full dark:!bg-[#141414]"
        style={{ flex: 1, backgroundColor: "#fbf8fc" }}
      >
        <Navbar showSideBar={showSideBar} setShowSideBar={setShowSideBar} />

        <div className="m-3 max-w-7xl mx-auto">
          {!isApproved && (
            <div className="relative overflow-hidden bg-white dark:!bg-[#181819] rounded-[24px] shadow-sm border border-gray-100 dark:!border-[#3c3a3d] p-6 md:p-8">
              <div className="absolute -top-16 -end-16 w-48 h-48 rounded-full bg-[#9b59bf]/10 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -start-10 w-40 h-40 rounded-full bg-purple-200/30 dark:bg-purple-900/20 blur-3xl pointer-events-none" />

              <div className="relative flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-purple-50 dark:bg-[#9b59bf]/15 text-[#9b59bf] border border-purple-100 dark:border-[#9b59bf]/25 mb-4">
                    <Sparkles size={13} />
                    {t("specialOfferPage.badge")}
                  </div>

                  <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white leading-tight max-w-2xl">
                    {t("specialOfferPage.title")}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400 mt-3 leading-relaxed max-w-2xl text-[15px]">
                    {t("specialOfferPage.subtitle")}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* CTA / Validation */}
          <div className="mt-5">
            {isChecking ? (
              <div className="w-full h-[88px] rounded-2xl bg-gray-200 dark:!bg-[#2a2a2a] animate-pulse" />
            ) : isApproved ||
              localSub?.dhdPartnership ||
              localSub?.plan === DHD_PLAN_NAME ? (
              <div className="space-y-4">
                <DhdPartnershipOffer subscription={localSub} />

                {warnings.length > 0 && (
                  <div className="bg-white dark:!bg-[#181819] rounded-2xl border border-purple-100 dark:!border-[#3c3a3d] shadow-sm overflow-hidden">
                    <div className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-purple-50 dark:!border-[#2a2a2a]">
                      <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-[#9b59bf]/15 text-[#9b59bf] flex items-center justify-center shrink-0">
                        <AlertTriangle size={18} strokeWidth={2.3} />
                      </div>
                      <div>
                        <h3 className="text-sm font-black text-gray-900 dark:text-white">
                          {t("dhdWarning.listTitle")}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t("dhdWarning.listSubtitle", {
                            count: warnings.length,
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="divide-y divide-purple-50 dark:!divide-[#2a2a2a]">
                      {warnings.map((w) => (
                        <div key={w.id} className="px-5 py-4">
                          <div className="flex items-start justify-between gap-3 mb-1.5">
                            <h4 className="text-sm font-bold text-gray-900 dark:text-white leading-snug">
                              {w.title}
                            </h4>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap">
                            {w.details}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : isActive ? (
              <ValidationDhd onStatusChange={refreshStatus} />
            ) : (
              <button
                type="button"
                onClick={() => setIsDhdOpen(true)}
                className="group w-full flex items-center justify-between gap-4 p-5 sm:p-6 rounded-2xl text-start text-white bg-gradient-to-br from-[#9b59bf] to-[#be97d4] shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 transition-all duration-300 active:scale-[0.99]"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
                    <span className="text-2xl">📝</span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-black text-lg leading-tight">
                      {t("specialOfferPage.formButtonTitle")}
                    </div>
                    <div className="text-sm font-medium text-white/80 mt-0.5">
                      {t("specialOfferPage.formButtonSubtitle")}
                    </div>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0 group-hover:bg-white/25 transition-colors">
                  <span className="text-lg">{isRTL ? "←" : "→"}</span>
                </div>
              </button>
            )}
          </div>

          {!isApproved && (
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white dark:!bg-[#181819] rounded-2xl p-5 border border-gray-100 dark:!border-[#3c3a3d] shadow-sm hover:border-purple-200 dark:hover:border-[#9b59bf]/40 transition-colors"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-purple-50 dark:bg-[#9b59bf]/15 text-[#9b59bf] flex items-center justify-center mb-3">
                      <Icon size={20} strokeWidth={2.25} />
                    </div>
                    <div className="text-[11px] font-bold text-[#9b59bf] mb-1 tracking-wide">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h3 className="font-black text-gray-900 dark:text-white mb-1.5 text-[15px]">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {!isApproved && (
            <div className="bg-gradient-to-br from-purple-50 to-white dark:!from-[#1b1a1d] dark:!to-[#181819] border border-purple-100 dark:!border-[#3c3a3d] rounded-2xl p-6 mt-6">
              <h2 className="text-base font-black text-gray-900 dark:text-white mb-3">
                {t("specialOfferPage.conditions.title")}
              </h2>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-400">
                {[1, 2, 3].map((n) => (
                  <li key={n} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#9b59bf] shrink-0" />
                    <span>{t(`specialOfferPage.conditions.item${n}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer note + secondary CTA */}
          <div className="mt-12 mb-32 flex flex-col items-center gap-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center max-w-md">
              {t("specialOfferPage.note")}
            </p>
          </div>
        </div>

        <Footer />
      </div>

      <Dhd
        isOpen={isDhdOpen}
        setIsOpen={setIsDhdOpen}
        onComplete={handleDhdComplete}
      />
    </div>
  );
};

export default SpecialOffer;
